var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { CANCELLATION_STATUS, FULFILLMENT_TYPE, STATUS_CODE, STATUS_DESCRIPTION, } from '../../constants/status';
import { retrieveTrackingNumbers } from '../returns/returns-helper';
import { dateFormat } from '../../components/order-details/util/format';
import { monthDay, weekDayMonthDay } from '../../constants/dateStrings';
import { normalize, formatPhoneNumber } from '../general';
var ONLINE = 'ONLINE';
var INSTORE = 'INSTORE';
export var getOrderType = function (orderNumber) {
    if (orderNumber && (orderNumber[0] === 'W' || orderNumber[0] === 'C')) {
        return ONLINE;
    }
    return INSTORE;
};
/**
 * Expected Output:
 * Initiated Returns -> "Return to UPS by (return by date)"
 * Completed Returns -> "Return Completed (return completion date)"
 * @returns {string} - The display header for initiated and completed returns
 */
export var getReturnsDisplayHeader = function (firstItem, isReturnInitiated, isReturnCompleted, isReturnInProgress, isPickupOrLORP) {
    if (isReturnInitiated) {
        if (isPickupOrLORP)
            return 'Return Pickup Requested';
        if (!(firstItem === null || firstItem === void 0 ? void 0 : firstItem.returnByDate) || firstItem.returnByDate === 'EXPIRED')
            return 'Return Initiated';
        return "Return to UPS by ".concat(dateFormat(firstItem.returnByDate, weekDayMonthDay));
    }
    if (isReturnCompleted) {
        if (!(firstItem === null || firstItem === void 0 ? void 0 : firstItem.statusUpdatedDate))
            return 'Return Completed';
        return "Return Completed ".concat(dateFormat(firstItem === null || firstItem === void 0 ? void 0 : firstItem.statusUpdatedDate, monthDay, true));
    }
    if (isReturnInProgress) {
        if (!(firstItem === null || firstItem === void 0 ? void 0 : firstItem.statusUpdatedDate))
            return 'Received by UPS';
        return "Received by UPS ".concat(dateFormat(firstItem === null || firstItem === void 0 ? void 0 : firstItem.statusUpdatedDate, monthDay, true));
    }
    return '';
};
export var getDisplayHeader = function (fg) {
    var fulfillmentType = fg.fulfillmentType, lineItems = fg.lineItems;
    var header;
    if (fulfillmentType) {
        if (fulfillmentType === FULFILLMENT_TYPE.ISP) {
            header = 'In-Store Purchase';
        }
        if ([
            FULFILLMENT_TYPE.STH,
            FULFILLMENT_TYPE.BODFS,
            FULFILLMENT_TYPE.APPLIANCE,
            FULFILLMENT_TYPE.DFS,
        ].includes(fulfillmentType)) {
            header = 'Delivery';
        }
        if ([
            FULFILLMENT_TYPE.BOPIS,
            FULFILLMENT_TYPE.BOSS,
            FULFILLMENT_TYPE.WILLCALL,
        ].includes(fulfillmentType)) {
            header = 'Pick Up';
        }
        if (fulfillmentType === FULFILLMENT_TYPE.MISC)
            header = 'Miscellaneous';
        if (fulfillmentType === FULFILLMENT_TYPE.CANCEL)
            header = 'Canceled Items';
        /*
        if the order is returned or return is initiated, the fulfillmentType doesnot get updated,
        so we need to check the lineItems so see what is the statusCode to update the displayHeader
        */
        var _a = getReturnStatus(fg), isInitiatedReturn = _a.isInitiatedReturn, isCompleteReturn = _a.isCompleteReturn, isInProgressReturn = _a.isInProgressReturn, isReturnedMultiReturn = _a.isReturnedMultiReturn, isJobSitePickup = _a.isJobSitePickup;
        if (isReturnedMultiReturn) {
            var trackingNumbers = retrieveTrackingNumbers(fg);
            var isReturnPickUp = isReturnOrderAPickUp(trackingNumbers, isJobSitePickup);
            return getReturnsDisplayHeader(lineItems === null || lineItems === void 0 ? void 0 : lineItems[0], isInitiatedReturn, isCompleteReturn, isInProgressReturn, isReturnPickUp);
        }
        if (isInitiatedReturn) {
            header = 'Return Initiated';
        }
        else if (isInProgressReturn) {
            header = 'Received by UPS';
        }
        else if (isCompleteReturn) {
            header = 'Return Completed';
        }
        else if (fulfillmentType) {
            switch (fulfillmentType) {
                case FULFILLMENT_TYPE.ISP:
                    header = 'In-Store Purchase';
                    break;
                case FULFILLMENT_TYPE.STH:
                case FULFILLMENT_TYPE.BODFS:
                case FULFILLMENT_TYPE.APPLIANCE:
                case FULFILLMENT_TYPE.DFS:
                    header = 'Delivery';
                    break;
                case FULFILLMENT_TYPE.BOPIS:
                case FULFILLMENT_TYPE.BOSS:
                    header = 'Pick Up';
                    break;
                case FULFILLMENT_TYPE.MISC:
                    header = 'Miscellaneous';
                    break;
                case FULFILLMENT_TYPE.CANCEL:
                    header = 'Canceled Items';
                    break;
                case FULFILLMENT_TYPE.ADDITIONAL_ITEMS:
                    header = 'Additional Items';
                    break;
                default:
                    break;
            }
        }
    }
    return header;
};
export var hasBackorderedItem = function (fulfillmentGroup) {
    var _a, _b, _c;
    var numOfBOItems = (_c = (_b = (_a = fulfillmentGroup.lineItems) === null || _a === void 0 ? void 0 : _a.filter(function (item) { return item.statusCode === STATUS_CODE.BACKORDERED; })) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0;
    return numOfBOItems > 0;
};
export var hasDelayedShipmentItems = function (fulfillmentGroup) {
    var _a, _b, _c;
    var numOfDelayedItems = (_c = (_b = (_a = fulfillmentGroup === null || fulfillmentGroup === void 0 ? void 0 : fulfillmentGroup.lineItems) === null || _a === void 0 ? void 0 : _a.filter(function (item) { return item.delayedShipment; })) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0;
    return numOfDelayedItems > 0;
};
// this function separates the canceled items from the fulfillmentgroups from the response, to render them separately.
export var processFulfillmentGroup = function (fulfillmentGroups) {
    // Separate Canceled Items
    var updatedFulfillmentGroup = fulfillmentGroups.flatMap(function (group) {
        var _a, _b, _c, _d, _e;
        var canceledLineItems = (_a = group === null || group === void 0 ? void 0 : group.lineItems) === null || _a === void 0 ? void 0 : _a.filter(function (item) { return item.statusCode === 140; });
        var cancellationStatus = group.cancellationStatus, oosRecoveryOptions = group.oosRecoveryOptions, storePickup = group.storePickup, fulfillmentType = group.fulfillmentType, actions = group.actions, fulfillmentStore = group.fulfillmentStore;
        var hasCanceledLineItem = canceledLineItems && canceledLineItems.length > 0;
        if (hasCanceledLineItem) {
            var canceledFulfillment = cancellationStatus === CANCELLATION_STATUS.OUT_OF_STOCK &&
                ((_b = group.actions) === null || _b === void 0 ? void 0 : _b.isOosRecoveryEnabled) &&
                ((_d = (_c = group.oosRecoveryOptions) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0) > 0
                ? {
                    fulfillmentType: fulfillmentType,
                    lineItems: canceledLineItems,
                    cancellationStatus: cancellationStatus,
                    oosRecoveryOptions: oosRecoveryOptions,
                    storePickup: storePickup,
                    actions: actions,
                    fulfillmentStore: fulfillmentStore,
                }
                : {
                    fulfillmentType: FULFILLMENT_TYPE.CANCEL,
                    lineItems: canceledLineItems,
                };
            var remainingLineItems = (_e = group === null || group === void 0 ? void 0 : group.lineItems) === null || _e === void 0 ? void 0 : _e.filter(function (item) { return item.statusCode !== 140; });
            if (remainingLineItems && remainingLineItems.length > 0) {
                var remainingFulfillment = __assign(__assign({}, group), { lineItems: remainingLineItems });
                return [remainingFulfillment, canceledFulfillment];
            }
            return [canceledFulfillment];
        }
        return [group];
    });
    return updatedFulfillmentGroup;
};
// Indexing Cancellation Items fulfillmentType to last Index of the fulfillment Array
export var moveCanceledObjectToLastIndex = function (fulfillmentTypeArray) {
    var _a, _b;
    var canceledItems = (_a = fulfillmentTypeArray === null || fulfillmentTypeArray === void 0 ? void 0 : fulfillmentTypeArray.filter) === null || _a === void 0 ? void 0 : _a.call(fulfillmentTypeArray, function (item) { return item.fulfillmentType === FULFILLMENT_TYPE.CANCEL; });
    var filteredOutArray = (_b = fulfillmentTypeArray === null || fulfillmentTypeArray === void 0 ? void 0 : fulfillmentTypeArray.filter) === null || _b === void 0 ? void 0 : _b.call(fulfillmentTypeArray, function (item) { return (item === null || item === void 0 ? void 0 : item.fulfillmentType) !== FULFILLMENT_TYPE.CANCEL; });
    return __spreadArray(__spreadArray([], filteredOutArray, true), canceledItems, true);
};
/**
 * Orders the provided array by the dates of the fulfillment groups
 */
export var orderReturnFGByDate = function (fulfillmentGroups) {
    fulfillmentGroups.sort(function (fgA, fgB) {
        var dateA = new Date(fgA.returnDate || '');
        var dateB = new Date(fgB.returnDate || '');
        if (dateA < dateB)
            return 1;
        if (dateA > dateB)
            return -1;
        return 0;
    });
};
/**
 * Util function for returning boolean checks for the status (returned, return initiated, etc) of the fulfillment group
 * This also uses the returnDate, since it's a field that is only added when it's a fg that comes from the multi-return changes.
 */
export var getReturnStatus = function (fulfillmentGroup) {
    var _a, _b, _c, _d;
    var status = (((_b = (_a = fulfillmentGroup === null || fulfillmentGroup === void 0 ? void 0 : fulfillmentGroup.lineItems) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.statusDescription) ||
        '');
    var isJobSitePickup = (_d = (_c = fulfillmentGroup === null || fulfillmentGroup === void 0 ? void 0 : fulfillmentGroup.lineItems) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.isJobSitePickup;
    var isCompleteReturn = status === STATUS_DESCRIPTION.RETURNED;
    var isInitiatedReturn = status === STATUS_DESCRIPTION.RETURN_INITIATED;
    var isInProgressReturn = status === STATUS_DESCRIPTION.RETURN_IN_PROGRESS;
    var isReturnStatus = [
        STATUS_DESCRIPTION.RETURNED,
        STATUS_DESCRIPTION.RETURN_INITIATED,
        STATUS_DESCRIPTION.RETURN_IN_PROGRESS,
    ].includes(status);
    var isReturnedMultiReturn = isReturnStatus && fulfillmentGroup.returnDate !== undefined;
    var isReturnedPreMultiReturn = isReturnStatus && fulfillmentGroup.returnDate === undefined;
    var isSoris = (fulfillmentGroup === null || fulfillmentGroup === void 0 ? void 0 : fulfillmentGroup.isSoris) || false;
    return {
        isCompleteReturn: isCompleteReturn,
        isInitiatedReturn: isInitiatedReturn,
        isInProgressReturn: isInProgressReturn,
        isReturnedMultiReturn: isReturnedMultiReturn,
        isReturnedPreMultiReturn: isReturnedPreMultiReturn,
        isSoris: isSoris,
        isJobSitePickup: isJobSitePickup,
    };
};
/**
 * Determines whether the print return label is enabled for a fulfillment group.
 *
 * @param fulfillmentGroup - The fulfillment group of the order.
 * @param fulfillmentStatus - The fulfillment status of the order.
 * @param isInitiatedReturn - Indicates whether a return has been initiated for the order.
 * @param trackingNumbers - The tracking numbers associated with the order.
 * @param isSoris - Indicates whether the order is a SORIS order.
 * @returns A boolean value indicating whether the print return label is enabled.
 */
export var isPrintReturnLabelEnabled = function (fulfillmentGroup, fulfillmentStatus, isInitiatedReturn, trackingNumbers, isSoris) {
    var _a;
    if (isSoris === void 0) { isSoris = false; }
    if (isSoris)
        return false;
    // print label is not necessary for pickup order
    var isPickUp = isUPSPickup(trackingNumbers);
    var upsTrackingNumbers = trackingNumbers.filter(function (trackingInfo) {
        return trackingInfo.startsWith('1Z');
    });
    var containsNonUpsNumber = trackingNumbers.length !== upsTrackingNumbers.length;
    // although all items in the group should contain the same returnByDate value, we are checking all items to be safe as we dont want to prevent someone from printing a label for any possible items that are still within the return window
    var isFulfillmentReturnWindowExpired = (_a = fulfillmentGroup === null || fulfillmentGroup === void 0 ? void 0 : fulfillmentGroup.lineItems) === null || _a === void 0 ? void 0 : _a.every(function (lineItem) { return lineItem.returnByDate === 'EXPIRED'; });
    var printReturnLabelEnabled = fulfillmentStatus.isFulfillmentSTHorBOSS &&
        isInitiatedReturn &&
        !isFulfillmentReturnWindowExpired &&
        upsTrackingNumbers &&
        upsTrackingNumbers.length > 0 &&
        !containsNonUpsNumber &&
        !isPickUp;
    return printReturnLabelEnabled;
};
export var getEjKey = function (orderDetails, isB2CCustomer) {
    var _a, _b, _c, _d;
    var ejDate = orderDetails === null || orderDetails === void 0 ? void 0 : orderDetails.salesDate;
    var ejRegister = (_a = orderDetails === null || orderDetails === void 0 ? void 0 : orderDetails.registerNumber) === null || _a === void 0 ? void 0 : _a.toString().padStart(2, '0');
    var ejStore = (_b = orderDetails === null || orderDetails === void 0 ? void 0 : orderDetails.storeNumber) === null || _b === void 0 ? void 0 : _b.toString().padStart(4, '0');
    var ejSeqNo = isB2CCustomer
        ? (_c = orderDetails === null || orderDetails === void 0 ? void 0 : orderDetails.transactionId) === null || _c === void 0 ? void 0 : _c.toString().padStart(4, '0')
        : (_d = orderDetails === null || orderDetails === void 0 ? void 0 : orderDetails.transactionId) === null || _d === void 0 ? void 0 : _d.toString().padStart(5, '0').slice(0, 4);
    return { ejRegister: ejRegister, ejSeqNo: ejSeqNo, ejStore: ejStore, ejDate: ejDate };
};
export var isUPSPickup = function (trackingNumbers) {
    // returns true if any of the tracking numbers are for an order being picked up by UPS
    var upsTrackingNumbers = trackingNumbers.filter(function (trackingInfo) {
        return trackingInfo.startsWith('1Z');
    });
    return upsTrackingNumbers === null || upsTrackingNumbers === void 0 ? void 0 : upsTrackingNumbers.some(function (trackingInfo) {
        return trackingInfo.substring(8, 10) === '78' ||
            trackingInfo.substring(8, 10) === '26';
    });
};
export var isLORPReturn = function (trackingNumbers) {
    return trackingNumbers === null || trackingNumbers === void 0 ? void 0 : trackingNumbers.some(function (trackingInfo) { return (trackingInfo === null || trackingInfo === void 0 ? void 0 : trackingInfo[0]) === 'R'; });
};
/**
 * Checks if the order is a pickup order which currently includes:
 * - Jobsite Pickup
 * - LORP (Large Order Return Pickup)
 * - UPS Pickup
 * @returns {boolean} - Returns true if the order is a pickup order, false otherwise.
 */
export var isReturnOrderAPickUp = function (trackingNumbers, isJobSitePickup) {
    return isJobSitePickup ||
        isUPSPickup(trackingNumbers) ||
        isLORPReturn(trackingNumbers);
};
/**
 * Util function to build the delivery tracker lineItem request object, non STH ones will not have a lineItem field
 * @param fulfillmentGroup standard FG
 * @returns lineItem request object for STH fulfillment types required for delivery tracker request
 */
export var buildSTHDeliveryTrackerItems = function (fulfillmentGroup) {
    var _a;
    if ((fulfillmentGroup === null || fulfillmentGroup === void 0 ? void 0 : fulfillmentGroup.fulfillmentType) !== FULFILLMENT_TYPE.STH)
        return undefined;
    return (_a = fulfillmentGroup === null || fulfillmentGroup === void 0 ? void 0 : fulfillmentGroup.lineItems) === null || _a === void 0 ? void 0 : _a.map(function (lineItem) { return ({
        lineId: lineItem.lineId,
        returnWindow: lineItem.returnWindow,
    }); });
};
/**
 * Compares two address objects to determine if they are equal.
 * Only compares addressLine1, addressLine2, and zipCode fields after normalization.
 *
 * @returns {boolean} True if the addresses are equal, false otherwise.
 */
export function areAddressesEqual(firstAddress, secondAddress) {
    return (normalize(firstAddress.firstName) === normalize(secondAddress.firstName) &&
        normalize(firstAddress.lastName) === normalize(secondAddress.lastName) &&
        normalize(firstAddress.phoneNumber) ===
            normalize(secondAddress.phoneNumber) &&
        normalize(firstAddress.city) === normalize(secondAddress.city) &&
        normalize(firstAddress.state) === normalize(secondAddress.state) &&
        normalize(firstAddress.addressLine1) ===
            normalize(secondAddress.addressLine1) &&
        normalize(firstAddress.addressLine2) ===
            normalize(secondAddress.addressLine2) &&
        normalize(firstAddress.zipCode) === normalize(secondAddress.zipCode));
}
/**
 * Extracts and normalizes address data from a fulfillment group source.
 *
 * @param {IFulfillmentGroup['shippingAndHandling'] | IFulfillmentGroup['storePickup'] | IFulfillmentGroup['bodfsDeliveryAndHandling']} addressSource
 *   The specific address source object (shippingAndHandling, storePickup, or bodfsDeliveryAndHandling).
 * @param {string} fulfillmentType - The fulfillment type (e.g., 'sth', 'bopis', 'bodfs').
 */
export function extractAddressFromFulfillmentGroup(addressSource, fulfillmentType) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11, _12, _13, _14, _15, _16, _17, _18, _19, _20, _21;
    switch (fulfillmentType) {
        case FULFILLMENT_TYPE.STH: {
            var delivery = addressSource === null || addressSource === void 0 ? void 0 : addressSource.deliveryAddress;
            return {
                firstName: (_b = (_a = delivery === null || delivery === void 0 ? void 0 : delivery.name) === null || _a === void 0 ? void 0 : _a.firstName) !== null && _b !== void 0 ? _b : '',
                lastName: (_d = (_c = delivery === null || delivery === void 0 ? void 0 : delivery.name) === null || _c === void 0 ? void 0 : _c.lastName) !== null && _d !== void 0 ? _d : '',
                addressLine1: (_f = (_e = delivery === null || delivery === void 0 ? void 0 : delivery.postalDetails) === null || _e === void 0 ? void 0 : _e.addressLine1) !== null && _f !== void 0 ? _f : '',
                addressLine2: (_h = (_g = delivery === null || delivery === void 0 ? void 0 : delivery.postalDetails) === null || _g === void 0 ? void 0 : _g.addressLine2) !== null && _h !== void 0 ? _h : null,
                city: (_k = (_j = delivery === null || delivery === void 0 ? void 0 : delivery.postalDetails) === null || _j === void 0 ? void 0 : _j.city) !== null && _k !== void 0 ? _k : '',
                state: (_m = (_l = delivery === null || delivery === void 0 ? void 0 : delivery.postalDetails) === null || _l === void 0 ? void 0 : _l.state) !== null && _m !== void 0 ? _m : '',
                zipCode: (_p = (_o = delivery === null || delivery === void 0 ? void 0 : delivery.postalDetails) === null || _o === void 0 ? void 0 : _o.zipCode) !== null && _p !== void 0 ? _p : '',
                country: (_r = (_q = delivery === null || delivery === void 0 ? void 0 : delivery.postalDetails) === null || _q === void 0 ? void 0 : _q.country) !== null && _r !== void 0 ? _r : '',
                phoneNumber: formatPhoneNumber(((_s = delivery === null || delivery === void 0 ? void 0 : delivery.phoneNumbers) === null || _s === void 0 ? void 0 : _s.dayPhone) || ''),
            };
        }
        case FULFILLMENT_TYPE.BODFS: {
            var delivery = (_u = (_t = addressSource === null || addressSource === void 0 ? void 0 : addressSource.bodfsDeliveries) === null || _t === void 0 ? void 0 : _t[0]) === null || _u === void 0 ? void 0 : _u.deliveryAddress;
            return {
                firstName: (_w = (_v = delivery === null || delivery === void 0 ? void 0 : delivery.name) === null || _v === void 0 ? void 0 : _v.firstName) !== null && _w !== void 0 ? _w : '',
                lastName: (_y = (_x = delivery === null || delivery === void 0 ? void 0 : delivery.name) === null || _x === void 0 ? void 0 : _x.lastName) !== null && _y !== void 0 ? _y : '',
                addressLine1: (_0 = (_z = delivery === null || delivery === void 0 ? void 0 : delivery.postalDetails) === null || _z === void 0 ? void 0 : _z.addressLine1) !== null && _0 !== void 0 ? _0 : '',
                addressLine2: (_2 = (_1 = delivery === null || delivery === void 0 ? void 0 : delivery.postalDetails) === null || _1 === void 0 ? void 0 : _1.addressLine2) !== null && _2 !== void 0 ? _2 : null,
                city: (_4 = (_3 = delivery === null || delivery === void 0 ? void 0 : delivery.postalDetails) === null || _3 === void 0 ? void 0 : _3.city) !== null && _4 !== void 0 ? _4 : '',
                state: (_6 = (_5 = delivery === null || delivery === void 0 ? void 0 : delivery.postalDetails) === null || _5 === void 0 ? void 0 : _5.state) !== null && _6 !== void 0 ? _6 : '',
                zipCode: (_8 = (_7 = delivery === null || delivery === void 0 ? void 0 : delivery.postalDetails) === null || _7 === void 0 ? void 0 : _7.zipCode) !== null && _8 !== void 0 ? _8 : '',
                country: (_10 = (_9 = delivery === null || delivery === void 0 ? void 0 : delivery.postalDetails) === null || _9 === void 0 ? void 0 : _9.country) !== null && _10 !== void 0 ? _10 : '',
                phoneNumber: formatPhoneNumber(((_11 = delivery === null || delivery === void 0 ? void 0 : delivery.phoneNumbers) === null || _11 === void 0 ? void 0 : _11.dayPhone) || ''),
            };
        }
        case FULFILLMENT_TYPE.BOPIS: {
            var pickup = addressSource;
            var store = (_12 = pickup === null || pickup === void 0 ? void 0 : pickup.store) === null || _12 === void 0 ? void 0 : _12.address;
            return {
                firstName: (_15 = (_14 = (_13 = pickup === null || pickup === void 0 ? void 0 : pickup.pickUpPerson) === null || _13 === void 0 ? void 0 : _13.name) === null || _14 === void 0 ? void 0 : _14.firstName) !== null && _15 !== void 0 ? _15 : '',
                lastName: (_18 = (_17 = (_16 = pickup === null || pickup === void 0 ? void 0 : pickup.pickUpPerson) === null || _16 === void 0 ? void 0 : _16.name) === null || _17 === void 0 ? void 0 : _17.lastName) !== null && _18 !== void 0 ? _18 : '',
                addressLine1: '',
                addressLine2: null,
                city: '',
                state: '',
                zipCode: '',
                country: (_19 = store === null || store === void 0 ? void 0 : store.country) !== null && _19 !== void 0 ? _19 : '',
                phoneNumber: formatPhoneNumber(((_21 = (_20 = pickup === null || pickup === void 0 ? void 0 : pickup.pickUpPerson) === null || _20 === void 0 ? void 0 : _20.phoneNumbers) === null || _21 === void 0 ? void 0 : _21.dayPhone) || ''),
            };
        }
        default:
            return {
                firstName: '',
                lastName: '',
                addressLine1: '',
                addressLine2: null,
                city: '',
                state: '',
                zipCode: '',
                country: '',
                phoneNumber: '',
            };
    }
}
/**
 * Checks if an address object is considered empty.
 * Only validates the fields: addressLine1, addressLine2, and zipCode.
 * Returns true if all these fields are empty or falsy.
 *
 */
export function isAddressEmpty(address) {
    if (!address)
        return true;
    return !address.addressLine1 && !address.addressLine2 && !address.zipCode;
}
